/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CommunicationType } from '@gh/builder-shared-data';
import { createDialogRoute, FormState, FormStateBuilder, TextTypes, whenCreateOrEditMode } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_COUNTRY_CODE } from '@gh/config';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  FormMode,
  NotificationService,
  responsiveDialogOverlayPaneClass,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';
import { CommunicationsBusinessService, SmsDialogConfig } from '../../resources/communications-business.service';
import { CommunicationListItem, SmsItem } from '../../resources/communications.model';
import { SmsService } from '../../../services/sms.service';

@Component({
  selector: 'gh-sms-dialog',
  template: require('./sms-dialog.component.html'),
  styles: [require('./sms-dialog.component.scss')],
})
export class SmsDialogComponent implements OnInit, DialogInput<SmsDialogConfig> {
  input: SmsDialogConfig;
  form: FormState<SmsItem>;
  communicationListItem: CommunicationListItem;

  constructor(private dialogRef: MatDialogRef<any>,
              private formStateBuilder: FormStateBuilder,
              private businessService: CommunicationsBusinessService,
              private waitingService: WaitingService,
              private smsService: SmsService,
              private notificationService: NotificationService,
              private dialogService: DialogService) {

  }

  ngOnInit(): void {
    const sms = this.input.sms;
    const cellPhone = sms && sms.cellPhone;
    if (sms && sms.communicationId && !sms.isMarkedAsRead) {
      // tslint:disable-next-line:no-non-null-assertion
      this.businessService.readSms(sms.communicationId!).pipe(
        this.waitingService.operator(),
      ).subscribe((response) => {
        this.smsService.sendName();
        this.communicationListItem = response;
      });
    }
    const formMode = sms && sms.communicationId
    && (!this.input.mode || this.input.mode === FormMode.ViewMode) ? FormMode.ViewMode : FormMode.CreateMode;
    // add country code only if it does not exist in the phone
    let phone;
    if (formMode === FormMode.CreateMode) {
      const startPhoneValue = !cellPhone?.startsWith('+') ? `+${DEFAULT_COUNTRY_CODE}` : '+';
      phone =  `${startPhoneValue}${cellPhone ? cellPhone.replace(/\D/g, '') : ''}`;
    } else {
      phone = cellPhone;
    }

    this.form = this.formStateBuilder.buildPlain({
      value: {
        ...sms,
        cellPhone: phone,
      },
      mode: formMode,
      enabled: whenCreateOrEditMode(),
      children: {
        cellPhone: { required: true, type: TextTypes.Phone },
        template: void 0,
        message: { required: true },
        jobId: void 0,
      },
    });
  }

  loadSmsTemplates($event: AutocompleteLoadMoreEvent): void {
    const { load } = $event;

    load(this.businessService.getSmsTemplates());
  }

  onSelectTemplate(template: any): void {
    if (!template) {
      return;
    }
    this.businessService.getFilledSmsTemplate(template?.id, this.input?.contactId, this.input?.jobId).subscribe((res) => {
      this.form.build({
        cellPhone: this.form.value.cellPhone,
        message: res.smsTemplateDto.body,
        jobId: this.form.value.jobId,
        template: this.form.value.template,
      });

      if (res.unresolvedPlaceholders.length) {
        const unresolved = res.unresolvedPlaceholders.join(', ');
        this.notificationService.warning(`There are no values for ${unresolved}`);
      }
    });
  }

  onCancel(): void {
    if (this.form.group.dirty) {
      this.dialogService
        .confirmation(yesNoConfirmation('msg_changes_not_saved')).pipe(
        filter$(yesAnswer))
        .subscribe(() => this.dialogRef.close());
    } else {
      this.dialogRef.close();
    }
  }

  onOk(): void {
    this.dialogRef.close(this.communicationListItem);
  }

  onSend(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { sms, contactId } = this.input;

    this.businessService.sendSms(this.input.entryPoint, this.input, {
      ...{
        cellPhone: this.form.value.cellPhone,
        jobId: this.form.value.jobId,
        message: this.form.value.message,
      },
      communicationType: CommunicationType.OutgoingSms,
      contactId: <ID>(sms && sms.contactId || contactId),
      isMarkedAsRead: true,
      contactTypeCode: this.input.sms!.contactTypeCode, // tslint:disable-line:no-non-null-assertion
    }).pipe(
      this.waitingService.operator<CommunicationListItem>())
      .subscribe(
        (communicationListItem: CommunicationListItem) => {
          this.notificationService.success('msg_sms_sent_successfully');
          this.dialogRef.close(communicationListItem);
          // tslint:disable-next-line:align
        },      (error) => {
          if (error && error.messages[0]?.key === 'msg_invalid_phone_number') {
            this.notificationService.error('msg_invalid_phone_number', { countryCode: `+${DEFAULT_COUNTRY_CODE}`});
            return;
          }
          this.notificationService.processAll(error);
        });
  }
}

export const smsDialogRoute = createDialogRoute({
  component: SmsDialogComponent,
  config: { disableClose: true, panelClass: responsiveDialogOverlayPaneClass() },
});
